import React, { Suspense, lazy, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Provider } from "react-redux";
import { store, AuthProvider } from "@time-theft-analyzer/shared";
import { Toaster } from "react-hot-toast";
import PrivateRoute from "./components/PrivateRoute";
import AppSelector from "./pages/AppSelector";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import "./index.css";

// Lazy load all apps for better performance
const TimeTheftApp = lazy(() => import("./apps/TimeTheft/App"));
const CRMApp = lazy(() => import("./apps/CRM/App"));
const AdminApp = lazy(() => import("./apps/Admin/App"));
const NotificationsApp = lazy(() => import("./apps/Notifications/App"));
const ATSApp = lazy(() => import("./apps/ATS/App"));
const HRMApp = lazy(() => import("./apps/HRM/App"));
const FinanceApp = lazy(() => import("./apps/Finance/App"));
const AssetsApp = lazy(() => import("./apps/Assets/App"));
const TaskManagerApp = lazy(() => import("./apps/TaskManager/App"));
const DocumentsApp = lazy(() => import("./apps/Documents/App"));
const AutomationsApp = lazy(() => import("./apps/Automations/App"));
const LMSApp = lazy(() => import("./apps/LMS/App"));
const AgentOpsApp = lazy(() => import("./apps/AgentOps/App"));

const APP_TITLES: Record<string, string> = {
  '/time-theft': 'Time Theft Analyzer',
  '/crm': 'CRM',
  '/admin': 'Admin',
  '/notifications': 'Notifications',
  '/ats': 'ATS',
  '/hrm': 'HRM',
  '/finance': 'Finance',
  '/assets': 'Assets',
  '/tasks': 'Task Manager',
  '/documents': 'Documents',
  '/automations': 'Automations',
  '/lms': 'LMS',
  '/agentops': 'AgentOps',
};

const BASE_TITLE = 'Inzint App Suite';

function TitleUpdater() {
  const location = useLocation();

  useEffect(() => {
    const pathPrefix = '/' + location.pathname.split('/')[1];
    const appName = APP_TITLES[pathPrefix];
    document.title = appName ? `${appName} | ${BASE_TITLE}` : BASE_TITLE;
  }, [location.pathname]);

  return null;
}

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading application...</p>
    </div>
  </div>
);

function App() {
  return (
    <Provider store={store}>
      <Router>
        <TitleUpdater />
        <AuthProvider>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#10b981',
                  secondary: '#fff',
                },
              },
              error: {
                duration: 4000,
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#fff',
                },
              },
            }}
          />
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/auth/callback" element={<AuthCallback />} />

              {/* App Selector - Landing Page */}
              <Route
                path="/"
                element={
                  <PrivateRoute>
                    <AppSelector />
                  </PrivateRoute>
                }
              />

              {/* Time Theft Analyzer App */}
              <Route
                path="/time-theft/*"
                element={
                  <PrivateRoute requiredApp="time-camp">
                    <TimeTheftApp />
                  </PrivateRoute>
                }
              />

              {/* CRM App */}
              <Route
                path="/crm/*"
                element={
                  <PrivateRoute requiredApp="crm">
                    <CRMApp />
                  </PrivateRoute>
                }
              />

              {/* Admin App */}
              <Route
                path="/admin/*"
                element={
                  <PrivateRoute requiredApp="admin">
                    <AdminApp />
                  </PrivateRoute>
                }
              />

              {/* Notifications App */}
              <Route
                path="/notifications/*"
                element={
                  <PrivateRoute requiredApp="notifications">
                    <NotificationsApp />
                  </PrivateRoute>
                }
              />

              {/* ATS App */}
              <Route
                path="/ats/*"
                element={
                  <PrivateRoute requiredApp="ats">
                    <ATSApp />
                  </PrivateRoute>
                }
              />

              {/* HRM App */}
              <Route
                path="/hrm/*"
                element={
                  <PrivateRoute requiredApp="hrm">
                    <HRMApp />
                  </PrivateRoute>
                }
              />

              {/* Finance App */}
              <Route
                path="/finance/*"
                element={
                  <PrivateRoute requiredApp="finance">
                    <FinanceApp />
                  </PrivateRoute>
                }
              />

              {/* Assets App */}
              <Route
                path="/assets/*"
                element={
                  <PrivateRoute requiredApp="assets">
                    <AssetsApp />
                  </PrivateRoute>
                }
              />

              {/* Task Manager App */}
              <Route
                path="/tasks/*"
                element={
                  <PrivateRoute requiredApp="tasks">
                    <TaskManagerApp />
                  </PrivateRoute>
                }
              />

              {/* Documents App */}
              <Route
                path="/documents/*"
                element={
                  <PrivateRoute requiredApp="documents">
                    <DocumentsApp />
                  </PrivateRoute>
                }
              />

              {/* Automations App */}
              <Route
                path="/automations/*"
                element={
                  <PrivateRoute requiredApp="automations">
                    <AutomationsApp />
                  </PrivateRoute>
                }
              />

              {/* LMS App */}
              <Route
                path="/lms/*"
                element={
                  <PrivateRoute requiredApp="lms">
                    <LMSApp />
                  </PrivateRoute>
                }
              />

              {/* AgentOps App */}
              <Route
                path="/agentops/*"
                element={
                  <PrivateRoute requiredApp="agentops">
                    <AgentOpsApp />
                  </PrivateRoute>
                }
              />

              {/* Catch all - redirect to app selector */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </Router>
    </Provider>
  );
}

export default App;
